import { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";

import AppDialog from "../General/Dialog/AppDialog";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getAuctioneers } from "../../services/AuctioneerService";
import { assignDocumentAuctioneer } from "../../services/LegalFileAttachmentService";

const label = (a) => (a ? `${a.full_name} · ${a.enrollment}` : "");

/**
 * Elegir del registro el martillero de una Designación de Martillero. El nombre
 * y la matrícula del escrito salen de ese martillero.
 */
export default function AuctioneerAssignDialog({ open, document, onClose, onDone }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !document) return;
    setValue(document.auctioneer ?? null);
    setInput("");
  }, [open, document]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await getAuctioneers({ search: input.trim(), active_only: 1, perPage: 15 });
        setOptions(res.data?.data ?? []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [input, open]);

  const readEnrollment = document?.extracted_data?.matricula_martillero;
  const readName = document?.extracted_data?.martillero;

  const save = async () => {
    if (!value) return;
    setSaving(true);
    try {
      const res = await assignDocumentAuctioneer(document.id, value.id);
      showToast(res.data?.message ?? "Martillero asignado.", "success");
      onDone?.();
      onClose();
    } catch (err) {
      showToast(err?.response?.data?.message ?? "No se pudo asignar el martillero.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={() => !saving && onClose()}
      title="Martillero de la designación"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !value}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ fontWeight: 800 }}
          >
            Asignar
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Lo leído del PDF: <b>{readName || "—"}</b>
          {" · "}matrícula <b>{readEnrollment || "—"}</b>. El escrito usa el nombre y la matrícula del martillero que
          elijas acá.
        </Typography>

        <Autocomplete
          options={options}
          value={value}
          onChange={(_, v) => setValue(v)}
          inputValue={input}
          onInputChange={(_, v, reason) => reason !== "reset" && setInput(v)}
          filterOptions={(x) => x}
          loading={loading}
          getOptionLabel={label}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="No hay martilleros que coincidan"
          renderInput={(params) => (
            <TextField {...params} label="Buscar por nombre o matrícula" size="small" autoFocus />
          )}
        />

        {readEnrollment && !document?.auctioneer_id && (
          <Alert severity="info" variant="outlined">
            La matrícula {readEnrollment} no está en el registro.
            {hasPermission("administrar martilleros")
              ? " Podés registrarla en Configuración › Martilleros: al hacerlo, esta designación se asocia sola."
              : " Pedí que la registren en Configuración › Martilleros."}
          </Alert>
        )}
      </Box>
    </AppDialog>
  );
}
